import axios from "axios";

const state = () => ({
  config: {},
  sections: [],
  rangeSections: [],
  errors: null,
  loading: false,
  loading_sections: false,
  loading_range_sections: false,
  success: false,
  updating: false,
  generating_qr_code: false,
  qr_code_url: null,
});

const mutations = {
  SET_CONFIG(state, config) {
    state.config = config;
  },
  SET_SECTIONS(state, sections) {
    state.sections = sections;
  },
  SET_RANGE_SECTIONS(state, rangeSections) {
    state.rangeSections = rangeSections;
  },
  // `config` holds the saved values only, so write each one into its field
  // and keep the type and title the form is built from.
  UPDATE_CONFIG(state, config) {
    const updated = { ...state.config };
    Object.entries(config).forEach(([key, value]) => {
      updated[key] = { ...updated[key], value };
    });
    state.config = updated;
  },
  SET_ERRORS(state, errors) {
    state.errors = errors;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_LOADING_SECTIONS(state, loading) {
    state.loading_sections = loading;
  },
  SET_LOADING_RANGE_SECTIONS(state, loading) {
    state.loading_range_sections = loading;
  },
  SET_UPDATING(state, updating) {
    state.updating = updating;
  },
  SET_SUCCESS(state, success) {
    state.success = success;
  },
  CLEAR_STATUS(state) {
    state.errors = null;
    state.success = false;
  },
  SET_IS_GENERATING_QR_CODE(state, generating) {
    state.generating_qr_code = generating;
  },
  SET_QR_CODE_URL(state, url) {
    state.qr_code_url = url;
  },
};

const getters = {
  getConfig: (state) => {
    return state.config;
  },
  getSections: (state) => {
    return state.sections;
  },
  getRangeSections: (state) => {
    return state.rangeSections;
  },
  getErrors: (state) => {
    return state.errors;
  },
  isLoading: (state) => {
    return state.loading;
  },
  isLoadingSections: (state) => {
    return state.loading_sections;
  },
  isLoadingRangeSections: (state) => {
    return state.loading_range_sections;
  },
  isUpdating: (state) => {
    return state.updating;
  },
  getSuccessStatus: (state) => {
    return state.success;
  },
  isGeneratingQrCode: (state) => {
    return state.generating_qr_code;
  },
  getQrCodeUrl: (state) => {
    return state.qr_code_url;
  },
};

const actions = {
  async fetchConfig({ commit }, courseId) {
    commit("SET_LOADING", true);
    const url = `${process.env.VUE_APP_API_URL}/utilities/config/${courseId}`;
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${localStorage.getItem("user_token")}`,
    };

    try {
      const response = await axios.get(url, { headers });
      commit("SET_CONFIG", response.data);
    } catch (error) {
      commit("SET_CONFIG", {});

      if (error.response?.data?.message) {
        commit(
          "toastNotification/SET_MODAL",
          {
            title: "Error fetching config",
            message: `${
              error.response?.data?.message &&
              Array.isArray(error.response?.data?.message)
                ? error.response?.data?.message.join("\n")
                : error.response?.data?.message
            }`,
            type: "error",
          },
          { root: true }
        );
      } else {
        commit("SET_ERRORS", "Fetch config failed.");
      }
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async fetchSections({ commit }, courseId) {
    commit("SET_LOADING_SECTIONS", true);
    const url = `${process.env.VUE_APP_API_URL}/utilities/sections/${courseId}`;
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${localStorage.getItem("user_token")}`,
    };

    try {
      const response = await axios.get(url, { headers });
      commit("SET_SECTIONS", response.data);
    } catch (error) {
      commit("SET_SECTIONS", []);
      if (error.response?.data?.message) {
        commit(
          "toastNotification/SET_MODAL",
          {
            title: "Error fetching sections",
            message: `${
              error.response?.data?.message &&
              Array.isArray(error.response?.data?.message)
                ? error.response?.data?.message.join("\n")
                : error.response?.data?.message
            }`,
            type: "error",
          },
          { root: true }
        );
      } else {
        commit("SET_ERRORS", "Fetch sections failed.");
      }
    } finally {
      commit("SET_LOADING_SECTIONS", false);
    }
  },
  async fetchRangeSections({ commit }, courseId) {
    commit("SET_LOADING_RANGE_SECTIONS", true);
    const url = `${process.env.VUE_APP_API_URL}/utilities/drivingRangeSections/${courseId}`;
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${localStorage.getItem("user_token")}`,
    };

    try {
      const response = await axios.get(url, { headers });
      commit("SET_RANGE_SECTIONS", response.data);
    } catch (error) {
      commit("SET_RANGE_SECTIONS", []);
      if (error.response?.data?.message) {
        commit(
          "toastNotification/SET_MODAL",
          {
            title: "Error fetching range sections",
            message: `${
              error.response?.data?.message &&
              Array.isArray(error.response?.data?.message)
                ? error.response?.data?.message.join("\n")
                : error.response?.data?.message
            }`,
            type: "error",
          },
          { root: true }
        );
      } else {
        commit("SET_ERRORS", "Fetch range sections failed.");
      }
    } finally {
      commit("SET_LOADING_RANGE_SECTIONS", false);
    }
  },

  async updateConfig({ commit }, { courseId, payload, sendDevices }) {
    commit("SET_UPDATING", true);
    const url = `${process.env.VUE_APP_API_URL}/utilities/config/${courseId}${
      sendDevices ? "?devices=true" : ""
    }`;
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${localStorage.getItem("user_token")}`,
    };

    try {
      const response = await axios.patch(url, payload, { headers });
      commit("UPDATE_CONFIG", JSON.parse(response.config.data));
      commit("SET_SUCCESS", true);
      commit("SET_ERRORS", null);
      return { success: true };
    } catch (error) {
      commit("SET_SUCCESS", false);
      if (error.response?.data?.message) {
        commit(
          "toastNotification/SET_MODAL",
          {
            title: error.response?.data?.message,
            message: `${error.response?.data?.error.message}`,
            type: "error",
          },
          { root: true }
        );
      } else {
        commit("SET_ERRORS", "Update config failed.");
      }
      return { success: false };
    } finally {
      commit("SET_UPDATING", false);
      setTimeout(() => {
        commit("CLEAR_STATUS");
      }, 1000);
    }
  },

  async uploadSections({ commit }, { courseId, payload }) {
    commit("SET_LOADING", true);
    const url = `${process.env.VUE_APP_API_URL}/utilities/sections/${courseId}`;
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${localStorage.getItem("user_token")}`,
    };

    try {
      await axios.post(url, payload, { headers });
      commit("SET_SUCCESS", true);
      commit("SET_ERRORS", null);
    } catch (error) {
      commit("SET_SUCCESS", false);

      if (error.response?.data?.message) {
        commit(
          "toastNotification/SET_MODAL",
          {
            title: "Error uploading file",
            message: `${
              error.response?.data?.message &&
              Array.isArray(error.response?.data?.message)
                ? error.response?.data?.message.join("\n")
                : error.response?.data?.message
            }`,
            type: "error",
          },
          { root: true }
        );
      } else {
        commit("SET_ERRORS", "Upload sections failed.");
      }
    } finally {
      commit("SET_LOADING", false);
      setTimeout(() => {
        commit("CLEAR_STATUS");
      }, 1000);
    }
  },
  async uploadRangeSections({ commit }, { courseId, payload }) {
    commit("SET_LOADING", true);
    const url = `${process.env.VUE_APP_API_URL}/utilities/drivingRangeSections/${courseId}`;
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${localStorage.getItem("user_token")}`,
    };

    try {
      await axios.post(url, payload, { headers });
      commit("SET_SUCCESS", true);
      commit("SET_ERRORS", null);
    } catch (error) {
      commit("SET_SUCCESS", false);

      if (error.response?.data?.message) {
        commit(
          "toastNotification/SET_MODAL",
          {
            title: "Error uploading file",
            message: `${
              error.response?.data?.message &&
              Array.isArray(error.response?.data?.message)
                ? error.response?.data?.message.join("\n")
                : error.response?.data?.message
            }`,
            type: "error",
          },
          { root: true }
        );
      } else {
        commit("SET_ERRORS", "Upload range sections failed.");
      }
    } finally {
      commit("SET_LOADING", false);
      setTimeout(() => {
        commit("CLEAR_STATUS");
      }, 1000);
    }
  },

  async uploadMapAndFlags({ commit }, { courseId, payload }) {
    commit("SET_LOADING", true);
    const url = `${process.env.VUE_APP_API_URL}/course/setup/${courseId}`;
    const headers = {
      Accept: "application/json",
      Authorization: `Bearer ${localStorage.getItem("user_token")}`,
    };

    try {
      await axios.post(url, payload, { headers });
      commit("SET_SUCCESS", true);
      commit("SET_ERRORS", null);
    } catch (error) {
      commit("SET_SUCCESS", false);
      if (error.response.status === 413) {
        commit("SET_ERRORS", "Content too large.");
      } else if (error.response?.data?.message) {
        commit(
          "toastNotification/SET_MODAL",
          {
            title: error.response?.data?.message,
            message: (() => {
              const responseData = error.response?.data;

              if (
                responseData?.errors &&
                typeof responseData.errors === "object"
              ) {
                const errorMessages = [];
                Object.values(responseData.errors).forEach((errorArray) => {
                  if (Array.isArray(errorArray)) {
                    errorMessages.push(...errorArray);
                  }
                });
                return errorMessages.join("\n");
              }

              return Array.isArray(responseData?.message)
                ? responseData.message.join("\n")
                : responseData?.message || "";
            })(),
            type: "error",
          },
          { root: true }
        );
      } else {
        commit("SET_ERRORS", "Upload map and flags failed.");
      }
    } finally {
      commit("SET_LOADING", false);
      setTimeout(() => {
        commit("CLEAR_STATUS");
      }, 1000);
    }
  },
  async generateQrCode({ commit }, payload) {
    commit("SET_IS_GENERATING_QR_CODE", true);

    const url = `${
      process.env.VUE_APP_API_URL
    }/utilities/generate-range-qr-code?id=${payload.dropletName}&course=${
      payload.courseName
    }&preview=${0}`;

    const config = {
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + localStorage.getItem("user_token"),
      },
    };

    try {
      const response = await axios.get(url, config);
      commit("SET_QR_CODE_URL", response.data);
      commit("SET_IS_GENERATING_QR_CODE", false);
    } catch (error) {
      commit("SET_IS_GENERATING_QR_CODE", false);
      commit(
        "SET_ERRORS",
        error.response?.data?.message || "Failed to generate QR Code"
      );
    }
  },
  clearQrCodeUrl({ commit }) {
    commit("SET_QR_CODE_URL", null);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
